import { createReducer } from '@reduxjs/toolkit';
import { IProjectDto, ProjectDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    clearProject,
    selectClient,
    createNewEmptyProject,
    updateProjectName,
    saveProject,
    importProjectForProjectForm,
    updateProjectProjectManagers,
    importAllProjectManagers,
} from '../actions/projectActions';
import { SaveEntityStatus } from './common';
import { GraphUserDto } from '../../api/msGraphApi';

export interface ProjectReducerState {
    project: IProjectDto | null;
    allProjectManagers: GraphUserDto[] | null;

    saveStatus: SaveEntityStatus;
}

function clearState(state: ProjectReducerState): void {
    state.project = null;
    state.allProjectManagers = null;

    state.saveStatus = SaveEntityStatus.None;
}

function getInitialState(): ProjectReducerState {
    const state: ProjectReducerState = {
        project: null,
        allProjectManagers: null,

        saveStatus: SaveEntityStatus.None,
    };

    clearState(state);

    return state;
}

export const projectFormReducer = createReducer<ProjectReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importProjectForProjectForm.fulfilled, (state: ProjectReducerState, action: any) => {
                state.project = action.payload ?? new ProjectDto();
            })
            .addCase(createNewEmptyProject, (state: ProjectReducerState, action: any) => {
                state.project = new ProjectDto();
            })
            .addCase(selectClient, (state: ProjectReducerState, action: any) => {
                state.project! = { ...state.project, clientId: action.payload };
            })
            .addCase(clearProject, (state: ProjectReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateProjectName, (state: ProjectReducerState, action: any) => {
                myAssert(() => state.project !== null, 'There needs to be project if it is being updated');

                state.project! = { ...state.project, name: action.payload };
            })
            .addCase(updateProjectProjectManagers, (state: ProjectReducerState, action: any) => {
                myAssert(() => state.project !== null, 'There needs to be project if it is being updated');

                const newProjManagerId = action.payload;

                const newProject = new ProjectDto();
                newProject.init(state.project);

                if (!!newProject) {
                    if (!!newProjManagerId && newProjManagerId !== '') {
                        const newProjManagerIndex = state.project?.projectManagersAzObjIds?.findIndex(
                            (pmaoId) => pmaoId === newProjManagerId,
                        );

                        if (newProjManagerIndex !== undefined && newProjManagerIndex > -1) {
                            if (!!newProject?.projectManagersAzObjIds) {
                                newProject.projectManagersAzObjIds.splice(newProjManagerIndex, 1);
                            }
                        } else {
                            if (!!newProject?.projectManagersAzObjIds) {
                                newProject.projectManagersAzObjIds.unshift(newProjManagerId);
                            } else {
                                newProject.projectManagersAzObjIds = [newProjManagerId];
                            }
                        }

                        state.project = newProject;
                    }
                }
            })
            .addCase(importAllProjectManagers.fulfilled, (state: ProjectReducerState, action: any) => {
                const projManagers: GraphUserDto[] = action.payload;

                state.allProjectManagers = projManagers;
            })
            .addCase(saveProject.pending, (state: ProjectReducerState, action: any) => {
                myAssert(() => state.project !== null, 'There needs to be project if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveProject.fulfilled, (state: ProjectReducerState, action: any) => {
                myAssert(() => state.project !== null, 'Error saving project');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(saveProject.rejected, (state: ProjectReducerState, action: any) => {
                myAssert(() => state.project !== null, 'Error saving project');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
